import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Check, Leaf, Mail } from "lucide-react";
import { MaskLines, Eyebrow } from "./Reveal";
import { CONTACT, MONTHS } from "@/data/site";
import { scrollToHash, EASE } from "@/lib/scroll";
import { NOW, requestMonth } from "./CareCalendar";

const SeasonHint = () => (
  <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
    onClick={() => requestMonth(NOW)} data-testid="season-hint-button"
    className="group mb-5 w-full sm:w-auto inline-flex items-center gap-3 rounded-2xl sm:rounded-full bg-[#111511] text-white pl-2 pr-4 py-2 text-left transition-colors duration-300 hover:bg-[#2F5D3A]">
    <span className="relative shrink-0 w-8 h-8 rounded-full bg-[#9CC26A] text-[#111511] flex items-center justify-center">
      <Leaf className="w-4 h-4" />
      <span className="absolute inset-0 rounded-full bg-[#9CC26A] animate-ping opacity-30" />
    </span>
    <span className="flex-1 text-sm leading-snug">
      <span className="text-[#D8E6C4]">Jetzt im {MONTHS[NOW - 1].name}:</span>{" "}
      <span className="font-medium" data-testid="season-hint-text">{MONTHS[NOW - 1].season}</span>
    </span>
    <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-[#9CC26A] group-hover:text-white transition-colors">
      Anfragen <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
    </span>
  </motion.button>
);

const TRUST = ["Kostenloses Angebot", "Flexible Termine", "Saubere Arbeit"];

const HeroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.35], [14, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.35], [0.92, 1]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);
  return (
    <div ref={ref} className="[perspective:1400px] mt-12 lg:mt-16">
      <motion.div style={{ rotateX, scale }} className="origin-top relative overflow-hidden rounded-[28px] lg:rounded-[36px] aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/9] bg-[#DDE3D5]">
        <motion.img initial={{ scale: 1.25, opacity: 0 }} animate={{ scale: 1.12, opacity: 1 }} transition={{ duration: 1.8, ease: EASE, delay: 0.4 }}
          style={{ y: imgY }} src="/images/garden-hero.jpg" alt="Gepflegter Garten mit frisch gemähtem Rasen – Gartenpflege in Bottrop"
          className="absolute inset-0 w-full h-full object-cover object-[50%_70%]" data-testid="hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 1.3 }}
          className="absolute left-4 right-4 bottom-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[340px] rounded-2xl bg-white/80 backdrop-blur-xl p-5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]">
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#2F5D3A]">Gartenpflege nach Bedarf</p>
          <p className="mt-2 font-head text-lg font-semibold leading-snug text-[#111511]">Einmalig, 14-tägig oder regelmäßig – Sie entscheiden.</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="hero" data-testid="hero-section" className="relative pt-32 sm:pt-36 lg:pt-40 pb-10">
    <div className="absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(60%_60%_at_80%_0%,#E4EED6_0%,transparent_70%)] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
        <div className="lg:col-span-8">
          <SeasonHint />
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Eyebrow>Gartenservice Bottrop</Eyebrow>
          </motion.div>
          <h1 data-testid="hero-main-title" className="mt-6 font-head font-semibold text-[#111511] tracking-[-0.045em] leading-[0.98] text-[2.9rem] sm:text-6xl lg:text-[5.6rem]">
            <MaskLines delay={0.3} lines={["Zuverlässige", "Gartenpflege", <span className="text-[#5E8C3A]">in Bottrop</span>, "und Umgebung."]} />
          </h1>
        </div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.8 }} className="lg:col-span-4 lg:pb-3">
          <p className="text-base sm:text-lg leading-relaxed text-[#5B615A]">
            Rasen, Hecken, Beete, Laub und Unkraut – K&amp;A Gartenservice pflegt Ihren Garten gründlich, sauber und zu fairen Preisen.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row lg:flex-col gap-3">
            <button data-testid="hero-cta-button" onClick={() => scrollToHash("#anfrage")} className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#111511] text-white px-6 py-4 text-[15px] font-medium transition-colors duration-300 hover:bg-[#2F5D3A]">
              Kostenloses Angebot anfragen <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a data-testid="hero-phone-link" href={CONTACT.phoneHref} target="_top" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[#111511]/15 bg-white px-6 py-4 text-[15px] font-semibold text-[#111511] transition-colors duration-300 hover:border-[#111511]">
              <Phone className="w-4 h-4" /> {CONTACT.phone}
            </a>
          </div>
          <a data-testid="hero-email-link" href={CONTACT.emailHref} target="_top" className="mt-4 inline-flex items-center gap-2 text-[15px] font-medium text-[#30352F] underline-offset-4 hover:underline">
            <Mail className="w-4 h-4 text-[#2F5D3A]" /> {CONTACT.email}
          </a>
        </motion.div>
      </div>
      <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }} className="mt-8 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-trust-list">
        {TRUST.map((t) => (
          <li key={t} className="flex items-center gap-2 text-sm text-[#30352F]"><span className="w-5 h-5 rounded-full bg-[#E4EED6] text-[#2F5D3A] flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>{t}</li>
        ))}
      </motion.ul>
      <HeroImage />
    </div>
  </section>
);
